import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, BarChart, Bar, LineChart, Line,
  RadialBarChart, RadialBar, XAxis, Tooltip,
} from "recharts";
import { Reveal, SectionHeading, Counter } from "./primitives";
import { SERVICES } from "@/data/site";
import { useModal } from "./Forms";

export function Services() {
  const { openInstitution } = useModal();
  return (
    <section id="services" className="relative bg-ink-1 py-28" data-testid="services">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          overline="03 — What We Do"
          title={<>ONE PARTNER. <span className="text-gradient">MULTIPLE EDUCATION SOLUTIONS.</span></>}
          subtitle="Every capability an MBA/PGDM institution needs to grow — engineered to work as one system."
        />

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.n} delay={(i % 3) * 0.08} className={i === 0 ? "lg:col-span-1" : ""}>
              <motion.div
                whileHover={{ y: -6 }}
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-ink-2 p-7 transition-colors duration-300 hover:border-brand-cyan/40"
              >
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand-blue/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-cyan/12 text-brand-cyan transition-transform duration-300 group-hover:scale-110">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <span className="font-mono text-xs text-white/25">{s.n}</span>
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-white/50">{s.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {s.items.slice(0, 6).map((it) => (
                    <span key={it} className="rounded-full border border-white/8 bg-white/5 px-2.5 py-1 text-[11px] text-white/55">{it}</span>
                  ))}
                  {s.items.length > 6 && (
                    <span className="rounded-full border border-brand-cyan/20 bg-brand-cyan/10 px-2.5 py-1 text-[11px] text-brand-cyan">+{s.items.length - 6} more</span>
                  )}
                </div>
                <button onClick={openInstitution} className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-medium text-white transition-colors hover:text-brand-cyan" data-testid={`service-cta-${s.n}`}>
                  {s.cta} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const trafficData = [40, 62, 55, 78, 90, 84, 110, 132, 120, 148].map((v, i) => ({ x: i, v }));
const leadsData = [30, 45, 38, 60, 72, 68, 88].map((v, i) => ({ x: i, v }));
const applData = [12, 18, 15, 24, 30, 28, 36, 42].map((v, i) => ({ x: i, v }));

const KPIS = [
  { label: "Website Traffic", value: 148, suffix: "K", trend: "+24%" },
  { label: "Leads", value: 12, suffix: ".4K", trend: "+18%" },
  { label: "Calls", value: 9, suffix: ".1K", trend: "+11%" },
  { label: "Applications", value: 3, suffix: ".2K", trend: "+29%" },
  { label: "Admissions", value: 1, suffix: ".1K", trend: "+21%" },
  { label: "Student Enquiries", value: 18, suffix: "K", trend: "+33%" },
];

export function FlagshipDashboard() {
  return (
    <section className="relative overflow-hidden bg-ink-0 py-28" data-testid="flagship">
      <div className="absolute inset-0 bg-radial-glow opacity-60" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <h2 className="max-w-4xl font-display text-3xl font-semibold leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
            IMAGINE YOUR DIGITAL, MARKETING, ADMISSIONS AND STUDENT ACQUISITION OPERATIONS UNDER ONE ROOF.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 font-display text-5xl font-bold lg:text-7xl">
            THAT'S <span className="text-gradient">A3.</span>
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-14 rounded-3xl border border-white/10 bg-ink-1/80 p-5 backdrop-blur-xl lg:p-7" data-testid="dashboard">
            <div className="mb-5 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-brand-accent/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-brand-cyan/70" />
                <span className="ml-3 font-mono text-xs uppercase tracking-widest text-white/40">A3 Growth Console</span>
              </div>
              <span className="hidden font-mono text-[11px] text-brand-cyan sm:block">● Live · Realtime</span>
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
              {KPIS.map((k, i) => (
                <div key={k.label} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-white/40">{k.label}</p>
                  <p className="mt-2 font-display text-2xl font-bold text-white">
                    <Counter to={k.value} duration={1.6} />{k.suffix}
                  </p>
                  <p className="mt-1 text-[11px] text-brand-cyan">{k.trend}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
              <ChartCard title="Website Traffic">
                <ResponsiveContainer width="100%" height={130}>
                  <AreaChart data={trafficData}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#C6FF3A" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#C6FF3A" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area type="monotone" dataKey="v" stroke="#C6FF3A" strokeWidth={2} fill="url(#g1)" />
                    <Tooltip contentStyle={tipStyle} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Leads / Enquiries">
                <ResponsiveContainer width="100%" height={130}>
                  <BarChart data={leadsData}>
                    <Bar dataKey="v" fill="#22C55E" radius={[4, 4, 0, 0]} />
                    <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={tipStyle} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Applications · Conversion Rate">
                <ResponsiveContainer width="100%" height={130}>
                  <LineChart data={applData}>
                    <Line type="monotone" dataKey="v" stroke="#FFB224" strokeWidth={2} dot={false} />
                    <Tooltip contentStyle={tipStyle} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

const tipStyle = { background: "#0B130C", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 };

function ChartCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-white/40">{title}</p>
      {children}
    </div>
  );
}
